<template>
  <div class="annotation-audit-log-list" :class="{ 'is-panel-layout': panelLayout }">
    <div class="annotation-audit-log-header" :class="{ 'is-title-hidden': !showTitle }">
      <h2 v-if="showTitle">{{ $t('annotation-audit-logs') }}</h2>
      <button class="button is-small" :class="{ 'is-loading': loading }" @click="reload">
        <span class="icon"><i class="fas fa-sync"></i></span>
      </button>
    </div>

    <div v-if="!loading && logs.length === 0" class="empty-state">
      {{ $t('no-audit-log-to-display') }}
    </div>

    <table v-else class="table is-fullwidth is-narrow">
      <tbody>
        <tr v-for="log in logs" :key="log.id">
          <td class="audit-time">{{ Number(log.created) | moment('lll') }}</td>
          <td>{{ log.actorUsername }}</td>
          <td>
            <span class="tag" :class="actionClass(log.action)">
              {{ $t(actionLabel(log.action)) }}
            </span>
          </td>
          <td class="audit-locate">
            <button
              v-if="canLocate(log)"
              class="button is-small"
              :title="$t('button-center-view-on-annot')"
              @click="locateAnnotation(log)"
            >
              <span class="icon"><i class="fas fa-map-marker-alt"></i></span>
            </button>
          </td>
          <td>
            <div>#{{ log.annotationIdent }} · {{ log.annotationType }}</div>
            <div v-if="log.details" class="audit-details">{{ log.details }}</div>
          </td>
        </tr>
      </tbody>
    </table>

    <button v-if="hasMore" class="button is-small is-fullwidth"
      :class="{ 'is-loading': loadingMore }" @click="loadMore">
      {{ $t('button-load-more') }}
    </button>
  </div>
</template>

<script>
import { AnnotationAuditLogCollection } from '@/api';

const pageSize = 50;

export default {
  name: 'annotation-audit-log-list',
  props: {
    image: { type: Number, default: null },
    annotation: { type: Number, default: null },
    index: { type: String, default: null },
    closeOnLocate: { type: Boolean, default: false },
    panelLayout: { type: Boolean, default: false },
    showTitle: { type: Boolean, default: true }
  },
  data() {
    return {
      logs: [],
      total: 0,
      loading: false,
      loadingMore: false
    };
  },
  computed: {
    hasMore() {
      return this.logs.length < this.total;
    }
  },
  watch: {
    image: 'reload',
    annotation: 'reload'
  },
  methods: {
    async fetchLogs(offset = 0) {
      if (!this.image && !this.annotation) {
        return null;
      }
      if (this.image) {
        return AnnotationAuditLogCollection.fetchByImage(this.image, { offset, max: pageSize });
      }
      return AnnotationAuditLogCollection.fetchByAnnotation(this.annotation, { offset, max: pageSize });
    },
    async reload() {
      this.loading = true;
      try {
        let collection = await this.fetchLogs(0);
        this.logs = collection ? collection.array : [];
        this.total = collection ? collection.totalNbItems : 0;
      } catch (error) {
        console.log(error);
        this.$notify({ type: 'error', text: this.$t('notif-error-fetch-annotation-audit-logs') });
      } finally {
        this.loading = false;
      }
    },
    async loadMore() {
      this.loadingMore = true;
      try {
        let collection = await this.fetchLogs(this.logs.length);
        this.logs = this.logs.concat(collection.array);
        this.total = collection.totalNbItems;
      } catch (error) {
        console.log(error);
        this.$notify({ type: 'error', text: this.$t('notif-error-fetch-annotation-audit-logs') });
      } finally {
        this.loadingMore = false;
      }
    },
    actionLabel(action) {
      return `audit-action-${String(action).toLowerCase()}`;
    },
    actionClass(action) {
      return {
        'is-success': action === 'CREATE',
        'is-info': action === 'UPDATE',
        'is-danger': action === 'DELETE'
      };
    },
    canLocate(log) {
      return log.action !== 'DELETE' && log.annotationIdent && log.image;
    },
    locateAnnotation(log) {
      this.$eventBus.$emit('selectAnnotation', {
        index: this.index || null,
        annot: {
          id: log.annotationIdent,
          image: log.image,
          project: log.project,
          type: log.annotationType
        },
        center: true,
        fromAuditLog: true
      });

      if (this.closeOnLocate && this.$parent && this.$parent.close) {
        this.$parent.close();
      }
    }
  },
  created() {
    this.reload();
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';

.annotation-audit-log-list {
  box-sizing: border-box;
  width: 34rem;
  max-width: calc(100vw - 2rem);
  max-height: 70vh;
  overflow-y: auto;
  padding: 1rem;
  border-radius: 6px;
  background: $dark-bg-primary;
  color: $dark-text-primary;
}

.annotation-audit-log-list.is-panel-layout {
  width: 100%;
  max-width: none;
  height: 100%;
  max-height: none;
  border-radius: 0;
  overflow-y: auto;
}

.annotation-audit-log-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.annotation-audit-log-header.is-title-hidden {
  justify-content: flex-end;
}

h2 {
  font-size: 1rem;
  font-weight: 600;
}

.table {
  background: transparent;
  color: $dark-text-primary;
}

.table td {
  color: $dark-text-primary;
  border-color: $dark-table-border;
  vertical-align: middle;
}

.audit-time {
  white-space: nowrap;
}

.audit-locate {
  width: 2.4rem;
  text-align: center;
}

.audit-locate .button {
  background: $dark-button-bg;
  border-color: $dark-button-border;
  color: $dark-text-primary;
}

.audit-locate .button:hover {
  background: $dark-button-hover-bg;
}

.audit-details {
  color: $dark-text-secondary;
  font-size: 0.78rem;
  line-height: 1.25;
  margin-top: 0.15rem;
  overflow-wrap: anywhere;
}

.empty-state {
  color: $dark-text-secondary;
  padding: 1rem 0;
  text-align: center;
}
</style>
